import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import { useAuth } from './auth-context';
import { fetchUserBots, getConversationIds, getChatHistory, sendMessage as apiSendMessage, deleteChatHistory, deleteAllChatHistory } from '@/api';

/**
 * Represents a single message in a chat session.
 */
export interface Message {
  /** The sender of the message. */
  type: 'user' | 'bot';
  /** The content of the message. */
  text: string;
  /** ISO timestamp of when the message was sent. */
  timestamp: string;
}

/**
 * Represents a chat session or conversation.
 */
export interface ChatSession {
  /** Unique identifier for the session. */
  sessionId: string;
  /** Optional ID of the agent associated with this session. */
  agentId?: string;
  /** ISO timestamp of when the session was created. */
  created: string;
  /** ISO timestamp of the last update to the session. */
  lastUpdated: string;
  /** A brief summary of the conversation. */
  summary: string;
  /** Optional contextual snippet from a search match. */
  snippet?: string;
  /** Optional list of messages in this session. */
  messages?: Message[];
}

/**
 * A suggested prompt for a chatbot.
 */
export interface Prompt {
  /** The display title for the prompt. */
  title: string;
  /** The actual message content of the prompt. */
  content: string;
  /** Optional icon associated with the prompt. */
  icon: string | null;
}

/**
 * Configuration and metadata for a specific chatbot agent.
 */
export interface Chatbot {
  /** The agent's unique ID. */
  agentId: string;
  /** The chatbot's internal ID. */
  id: string;
  /** The Google Cloud project ID. */
  projectId: string;
  /** A list of starter prompts for this chatbot. */
  prompts: Prompt[];
  /** A subtitle or description for the chatbot. */
  subtitle: string;
  /** The display title of the chatbot. */
  title: string;
}

/**
 * The shape of the account context state and actions.
 */
interface AccountContextType {
    /** The role of the logged-in user. */
    accountType: 'user' | 'admin';
    /** Updates the account type. */
    setAccountType: (type: 'user' | 'admin') => void;
    /** The list of chatbots the user is authorized to use. */
    allowedBots: Chatbot[] | null;
    /** Updates the list of allowed bots. */
    setAllowedBots: (bots: Chatbot[] | null) => void;
    /** The list of existing chat conversations. */
    conversations: ChatSession[] | null;
    /** Updates the list of conversations. */
    setConversations: (conversations: ChatSession[] | null) => void;
    /** The ID of the currently active chat session. */
    currentSessionId: string | null;
    /** Sets the current session ID. */
    setCurrentSessionId: (id: string | null) => void;
    /** The messages in the current session. */
    currentMessages: Message[];
    /** Updates the current messages. */
    setCurrentMessages: (messages: Message[]) => void;
    /** Loads a specific session's history by ID. */
    loadSession: (sessionId: string) => Promise<void>;
    /** Resets the state to start a new chat session. */
    startNewSession: () => void;
    /** Re-fetches the list of conversations from the API. */
    refreshConversations: () => Promise<void>;
    /** Locally adds a message to the current message list. */
    addMessage: (message: Message) => void;
    /** Deletes a session and refreshes the conversation list. */
    deleteSession: (sessionId: string) => Promise<void>;
    /** Deletes all sessions and refreshes the conversation list. */
    deleteAllSessions: () => Promise<void>;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

/**
 * Props for the AccountProvider component.
 */
interface AccountProviderProps {
    /** The child components that will have access to the context. */
    children?: ReactNode;
}

/**
 * Provides account and chat-related state to the application.
 * Manages allowed bots, conversation history, and the current active session.
 * 
 * @param {AccountProviderProps} props - The component props.
 * @returns {JSX.Element} The context provider component.
 */
export const AccountProvider: React.FC<AccountProviderProps> = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const [accountType, setAccountType] = useState<'user' | 'admin'>('user');
    const [allowedBots, setAllowedBots] = useState<Chatbot[] | null>(null);
    const [conversations, setConversations] = useState<ChatSession[] | null>(null);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const [currentMessages, setCurrentMessages] = useState<Message[]>([]);

    /**
     * Re-fetches the list of conversation IDs for the current user.
     */
    const refreshConversations = useCallback(async () => {
        if (user?.nyssoguid) {
            try {
                const convs = await getConversationIds();
                if (convs) {
                    setConversations(convs);
                }
            } catch (error) {
                console.error('Error refreshing conversations:', error);
            }
        }
    }, [user]);

    /**
     * Loads the message history for a given session ID.
     */
    const loadSession = useCallback(async (sessionId: string) => {
        try {
            const history = await getChatHistory(sessionId);
            if (history) {
                setCurrentSessionId(sessionId);
                setCurrentMessages(history);
            }
        } catch (error) {
            console.error('Error loading session:', error);
        }
    }, []);

    /**
     * Resets the current session state.
     */
    const startNewSession = useCallback(() => {
        setCurrentSessionId(null);
        setCurrentMessages([]);
    }, []);

    /**
     * Adds a single message to the current message history.
     */
    const addMessage = useCallback((message: Message) => {
        setCurrentMessages(prev => [...prev, message]);
    }, []);

    /**
     * Deletes a chat session and handles state cleanup if the deleted session was active.
     */
    const deleteSession = useCallback(async (sessionId: string) => {
        try {
            await deleteChatHistory(sessionId);
            if (currentSessionId === sessionId) {
                startNewSession();
            }
            await refreshConversations();
        } catch (error) {
            console.error('Error deleting session:', error);
        }
    }, [currentSessionId, startNewSession, refreshConversations]);

    /**
     * Deletes all chat sessions and handles state cleanup.
     */
    const deleteAllSessions = useCallback(async () => {
        try {
            await deleteAllChatHistory();
            startNewSession();
            await refreshConversations();
        } catch (error) {
            console.error('Error deleting all sessions:', error);
        }
    }, [startNewSession, refreshConversations]);

    // Initial data fetch on mount or user change
    useEffect(() => {
        const fetchAccountData = async () => {
            if (!authLoading && user) {
                const userId = user.nyssoguid;
                if (userId) {
                    try {
                        const bots = await fetchUserBots(userId);
                        if (bots){
                            setAllowedBots(bots);
                        } 
                        await refreshConversations();
                    } catch (error) {
                        console.error('Error fetching account data:', error);
                    }
                }
            }
        };

        fetchAccountData();
    }, [user, authLoading, refreshConversations]);

    return (
        <AccountContext.Provider value={{ 
            accountType, setAccountType, 
            allowedBots, setAllowedBots, 
            conversations, setConversations,
            currentSessionId, setCurrentSessionId,
            currentMessages, setCurrentMessages,
            loadSession, startNewSession, refreshConversations,
            addMessage, deleteSession, deleteAllSessions
        }}>
            {children}
        </AccountContext.Provider>
    );
};

/**
 * Custom hook to access the account context.
 * Must be used within an AccountProvider.
 * 
 * @throws {Error} If used outside of an AccountProvider.
 * @returns {AccountContextType} The account context value.
 */
export const useAccount = () => {
    const context = useContext(AccountContext);
    if (context === undefined) {
        throw new Error('useAccount must be used within an AccountProvider');
    }
    return context;
};
